import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { answerScriptStrings } from "@/lib/answerScript/i18n";
import { isFramed } from "@/components/AnswerScriptButton";
import { getInterviewReturnTarget } from "@/lib/activeInterviewNavigation";

// The answer script for beta testers the admin has switched on (2026-11-04): one
// answer to read aloud per question of the tester's own interview, with what the
// interviewer should do next. Opened in a new tab from the interview's start screen.
// Only the script: "Run it" and simulated interviews stay admin-only.

export function caseLabel(s, row) {
  const name = String(row.case || "").split(":")[0];
  if (name === "missing") return `${s.cases.missing} ${row.target || ""}`.trim();
  return s.cases[name] || name;
}

export default function AnswerScript() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Opened in place of the interview (inside a preview frame): a way back to it.
  const framed = isFramed();
  const [english, setEnglish] = useState("");
  // Nothing is written until the tester picks the English level and asks (2026-11-05):
  // each script costs AI calls and counts towards the 5 per interview.
  const [request, setRequest] = useState(null); // {n, english}
  const [script, setScript] = useState(null);
  const [error, setError] = useState("");
  const [lang, setLang] = useState(user?.preferred_language || "en");
  const s = answerScriptStrings(lang);

  useEffect(() => {
    if (!request) return undefined;
    let alive = true;
    setScript(null);
    setError("");
    api.post(`/sessions/${encodeURIComponent(sessionId)}/answer-script`, null,
      { timeout: 120000, params: request.english ? { english: request.english } : {} })
      .then(({ data }) => {
        if (!alive) return;
        setScript(data);
        if (data.interface_language) setLang(data.interface_language);
      })
      .catch((e) => alive && setError(e?.response?.data?.detail?.code || "other"));
    return () => { alive = false; };
  }, [sessionId, request]);

  const busy = Boolean(request) && !script && !error;
  const write = () => setRequest((r) => ({ n: (r?.n || 0) + 1, english }));
  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] print:bg-white" data-testid="answer-script">
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 space-y-6 print:p-0">
        <header className="space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {s.title}{script?.role_title ? `: ${script.role_title}` : ""}
          </h1>
          <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">{s.intro}</p>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <label className="text-sm flex items-center gap-2">
              <span className="text-[color:var(--text-secondary)]">{s.englishLabel}</span>
              <select value={english} onChange={(e) => setEnglish(e.target.value)} disabled={busy}
                className="rounded-full border border-[color:var(--border-stone)] bg-white px-3 py-1.5 text-sm" data-testid="answer-script-english">
                {Object.entries(s.english).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </label>
            <button type="button" onClick={write} disabled={busy || script?.writes_left === 0}
              className={`rounded-full px-4 py-1.5 text-sm disabled:opacity-50 ${request ? "border border-[color:var(--border-stone)] bg-white" : "bg-[color:var(--brand)] text-white font-medium"}`}
              data-testid={request ? "answer-script-again" : "answer-script-create"}>
              {request ? s.writeAgain : s.create}
            </button>
            <button type="button" onClick={() => window.print()} disabled={!script}
              className="rounded-full bg-[color:var(--brand)] text-white px-4 py-1.5 text-sm disabled:opacity-50" data-testid="answer-script-print">
              {s.print}
            </button>
            {framed && (
              <button type="button" onClick={() => navigate(getInterviewReturnTarget(`/interview/${encodeURIComponent(sessionId)}`))}
                className="rounded-full bg-[color:var(--brand)] text-white px-4 py-1.5 text-sm" data-testid="answer-script-back">
                {s.backToInterview}
              </button>
            )}
            {typeof script?.writes_left === "number" && (
              <span className="text-xs text-[color:var(--text-secondary)]" data-testid="answer-script-left">{s.writesLeft(script.writes_left)}</span>
            )}
          </div>
        </header>

        {!request && <p className="text-sm text-[color:var(--text-secondary)]" data-testid="answer-script-start">{s.chooseFirst}</p>}
        {busy && <p className="text-sm text-[color:var(--text-secondary)]" role="status">{s.writing}</p>}
        {error && <p className="text-sm text-rose-700" role="alert" data-testid="answer-script-error">{s.errors[error] || s.errors.other}</p>}
        {script && !script.follow_ups_enabled && (
          <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm" role="status">{s.followUpsOff}</p>
        )}

        {(script?.questions || []).map((row) => (
          <section key={row.number} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3 break-inside-avoid" data-testid={`answer-script-q${row.number}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">{s.question(row.number)}</h2>
              <span className="text-xs text-[color:var(--text-secondary)]">
                {caseLabel(s, row)}{row.english ? ` · ${s.level[row.english] || row.english}` : ""}
              </span>
            </div>
            <p className="text-sm text-[color:var(--text-secondary)]" lang="en">{row.text}</p>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand)]">{s.say}</div>
              <p className="mt-1 text-base leading-relaxed whitespace-pre-wrap" lang="en">{row.say}</p>
            </div>
            <p className="text-sm">
              <span className="font-medium">{s.expect} </span>
              {row.expect_kind === "none" && s.expectNone}
              {row.expect_kind === "repeat" && <>{s.expectRepeat} <q lang="en">{row.expect_text}</q></>}
              {row.expect_kind === "follow_up" && (row.expect_text
                ? <>{s.expectFollowUp} <q lang="en">{row.expect_text}</q></>
                : s.expectFollowUpAny)}
            </p>
            {row.note && <p className="text-xs text-amber-800">{s.note}</p>}
          </section>
        ))}
      </main>
    </div>
  );
}
